'use strict';

export default angular
  .module('maestro.services.data.organization', [
    'maestro.services.webcore'
  ])
  .service('OrganizationService', OrganizationService)
  .name;

function OrganizationService(webcore, $q, CacheService) {

  /**
   * Creates new organization from the supplied organization model.
   * @param organization
   * @param parentOrganizationId
   * @returns {Promise}
   */
  this.createOrganization = (organization, parentOrganizationId) => {
    this.getOrganizations.clearEntireCache();
    return $q.when(
      webcore.services.organization.createOrganization(organization, parentOrganizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates specified organization.
   * @param organizationId
   * @param organization
   * @returns {Promise}
   */
  this.updateOrganization = (organizationId, organization) => {
    let body = {
      organization: organization
    };
    this.getOrganizations.clearEntireCache();
    return $q.when(
      webcore.services.organization.updateOrganization(organizationId, body)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets a list of location narratives with organization scope
   * @param {number} organizationId
   * @param {Object} [params] Request parameters,
   * @param {number} [params.groupId] Filter by location's group ID.
   * @param {string|string[]} [params.searchTag] Filter by location's tags, multiple values allowed.
   * @param {number|number[]} [params.narrativeType] Filter by narrative type, multiple values allowed.
   * @param {number} [params.priority] Filter by priority higher or equal than that.
   * @param {number} [params.status] Filter by status, deleted are not returned by default.
   * @param {string} [sortBy] Property to sort narratives list by.
   * @param {string} [sortOrder] Sort order parameter (desc or asc).
   * @returns {Promise}
   */
  this.getOrganizationNarratives = (organizationId, params, sortBy = 'narrativeDateMs', sortOrder = 'desc') => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }
    if (!params) {
      params = {};
    }

    if (params.narrativeId) {
      params.rowCount = 1;
    } else {
      params.sortCollection = 'narratives';
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
      params.rowCount = params.rowCount || 100;
    }

    return $q.when(
      webcore.services.organization.getOrganizationNarratives(organizationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Subscribe for Location Narrative updates
   * @param {number} organizationId Organization ID.
   * @param params Request parameters
   * @param {number} params.locationId Location ID.
   * @param {number} [params.groupId] Filter by location's group ID.
   * @param {string|string[]} [params.searchTag] Filter by location's tag. Multiple values.
   * @param {number} [params.priority] Filter by priority higher or equal than that
   *   Details = 0,
   *   Info = 1,
   *   Warning = 2,
   *   Critical = 3
   * @param {number} [params.toPriority] Filter by priority less or equal than that
   *   Details = 0,
   *   Info = 1,
   *   Warning = 2,
   *   Critical = 3
   * @param {number} [params.status] Filter by status, deleted are not returned by default.
   *   Initial = 0,
   *   Deleted = 1,
   *   Resolved = 2
   * @param {string} [params.searchBy] Filter by title or description. Use * for a wildcard.
   * @param {string} [params.startDate] Narrative date range start
   * @param {string} [params.endDate] Narrative date range end
   */
  this.subscribeForOrganizationNarratives = (organizationId, params) => {
    return webcore.services.organization.subscribeForOrganizationNarratives(organizationId, params);
  };

  // #region Users in Organization ----------------------------------------

  /**
   * Updates specified user in organization with new status, notes and groupId.
   * Deprecated at Server 1.5
   * @returns {Promise}
   */
  this.updateUser = (organizationId, userId, status, notes, groupId) => {
    return $q.when(
      webcore.services.users.updateUserInfo(organizationId, userId, status, notes, groupId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates several users at once as a batch operation.
   * Deprecated at Server 1.5
   * @param organizationId
   * @param userIds
   * @param status
   * @param notes
   * @param groupId
   * @returns {Promise}
   */
  this.updateUsers = (organizationId, userIds, status, notes, groupId) => {
    return $q.when(
      webcore.services.organization.updateUsers(organizationId, userIds, status, notes, groupId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Invites users to the specified organization.
   * @param organizationId
   * @param invitees
   * @param isReInvite
   * @returns {Promise}
   */
  this.inviteUsers = (organizationId, invitees, isReInvite) => {
    return $q.when(
      webcore.services.organization.inviteUsers(organizationId, invitees, isReInvite)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Silently add users to specific organization.
   * @param organizationId
   * @param status
   * @param email
   * @returns {Promise}
   */
  this.addUsersSilent = (organizationId, status, email) => {
    return $q.when(
      webcore.services.user.searchUsersByEmail(email)
        .then(data => {
          if (!data.users) {
            return ($q.reject('No users with such email(s) found.'));
          }
          let userIds = data.users.map(user => {
            return user.id;
          });
          $q.resolve(this.updateUsers(organizationId, userIds, status));
        }, err => $q.reject(err))
    );
  };

  // #endregion

  /**
   * Gets organization's objects and properties.
   * @param organizationId
   * @returns {Promise}
   */
  this.getObjectsAndProperties = CacheService.cachePromiseFunc((organizationId) => {
    return $q.when(
      webcore.services.organization.listObjectsAndProperties(organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Saves updated objects and properties.
   * @param organizationId
   * @param objectsAndProperties
   * @returns {Promise}
   */
  this.updateObjectsAndProperties = (organizationId, objectsAndProperties) => {
    this.getObjectsAndProperties.clearEntireCache();
    return $q.when(
      webcore.services.organization.updateObjectsAndProperties(organizationId, angular.copy(objectsAndProperties))
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Uploads the specified large object (file) into the specified organization.
   * @param organizationId
   * @param objectFile
   * @param objectName
   * @param isPrivate
   * @param contentType
   * @param onUploadProgressCallback
   * @returns {Promise}
   */
  this.uploadLargeObject = (organizationId, objectFile, objectName, isPrivate, contentType, onUploadProgressCallback) => {
    return $q.when(
      webcore.services.organization.uploadLargeObject(
        organizationId, objectFile, objectName, { private: isPrivate }, contentType, onUploadProgressCallback)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Deletes specified object or property.
   * @param {number} organizationId
   * @param {string} objectOrPropertyName
   * @returns {Promise}
   */
  this.deleteObjectOrProperty = (organizationId, objectOrPropertyName) => {
    return $q.when(webcore.services.organization.deleteObjectOrProperty(organizationId, objectOrPropertyName)
      .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets particular object or property from organization
   * @param organizationId
   * @param objectOrPropertyName
   * @returns {Promise}
   */
  this.getObjectOrProperty = (organizationId, objectOrPropertyName) => {
    return $q.when(
      webcore.services.organization.listObjectsAndProperties(organizationId)
        .then(data => {
            let found = null;
            if (data.organizationObjects) {
              found = data.organizationObjects.find(o => o.name === objectOrPropertyName);
            }
            return $q.resolve(found);
          },
          err => $q.reject(err)
        )
    );
  };

  /**
   * Gets particular specified organization.
   * @param organizationId
   * @returns {Promise}
   */
  this.getOrganization = (organizationId) => {
    return $q.when(webcore.services.organization.getOrganizations(null, null, organizationId));
  };

  /**
   * Gets organizations allowed to be managed for the current user.
   * Allows filtering by orgId, domain name or name of the organizations to narrow down the results list.
   * @returns {Promise}
   */
  this.getOrganizations = CacheService.cachePromiseFunc((domainName, name, organizationId) => {
    return $q.when(
      webcore.services.organization.getOrganizations(domainName, name, organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err)));
  });

  /**
   * Deletes specified organziation
   * @param organizationId
   * @returns {Promise}
   */
  this.deleteOrganization = (organizationId) => {
    this.getOrganizations.clearEntireCache();
    return $q.when(
      webcore.services.organization.deleteOrganization(organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #region Administrators and Roles

  /**
   * Gets list of available roles.
   * @returns {Promise}
   */
  this.getRoles = CacheService.cachePromiseFunc(() => {
    return $q.when(
      webcore.services.organization.getRoles()
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Gets list of available brands.
   * @returns {Promise}
   */
  this.getBrands = CacheService.cachePromiseFunc(() => {
    return $q.when(
      webcore.services.organization.getBrands()
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Gets a list of administrators of the specified organization
   * @param {number} organizationId
   * @param {string} [sortBy] Property to sort administrator users list by.
   * @param {string} [sortOrder] Sort order parameter (desc or asc).
   * @returns {Promise}
   */
   this.getOrganizationAdmins = (organizationId, sortBy, sortOrder) => {
    return $q.when(
      webcore.services.organization.getOrganizationAdmins(organizationId, sortBy || 'lastName', sortOrder || 'desc')
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Grants specified role to the specified user.
   * @param userId
   * @param roleId
   * @returns {Promise}
   */
  this.grantRoleToUser = (userId, roleId) => {
    return $q.when(
      webcore.services.organization.grantRoleToUser(userId, roleId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Revoke administrative role from specified user.
   * @param userId
   * @returns {Promise}
   */
  this.revokeAdminRole = (userId) => {
    return $q.when(
      webcore.services.organization.revokeAdminRole(userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Adds user to the organization's admins.
   * @param {number} organizationId
   * @param {number} userId
   * @returns {Promise}
   */
  this.addUserToOrgAdmins = (organizationId, userId) => {
    return $q.when(
      webcore.services.organization.addUserToOrgAdmins(organizationId, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Removes user from the organization's admins.
   * @param organizationId
   * @param userId
   * @returns {Promise}
   */
  this.removeUserFromOrgAdmins = (organizationId, userId) => {
    return $q.when(
      webcore.services.organization.removeUserFromOrgAdmins(organizationId, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #endregion

}
